import { BooleanInput } from '@angular/cdk/coercion';
import { AsyncPipe, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { PerfilCompletoService } from 'app/core/user/perfil-completo.service';
import { AnilloPerfilComponent } from 'app/shared/components/anillo-perfil/anillo-perfil.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    standalone: true,
    styles: [`
        .pro-badge-inline {
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.5px;
            background: linear-gradient(135deg, #f59e0b, #d97706);
            color: #fff;
            padding: 1px 6px;
            border-radius: 4px;
            line-height: 1.4;
        }
        .pro-menu-item {
            color: var(--o-tx-d97706, #d97706) !important;
        }
        .pro-menu-item .mat-icon {
            color: var(--o-tx-d97706, #d97706) !important;
        }
    `],
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgClass,
        MatDividerModule,
        TranslocoPipe,
        AnilloPerfilComponent,
        AsyncPipe,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    /** Anillo de "perfil completo" alrededor del avatar. */
    readonly progresoPerfil$ = inject(PerfilCompletoService).progreso$;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _authService: AuthService,
        private _transloco: TranslocoService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user || {
                    id: '0',
                    name: this._transloco.translate('userMenu.fallbackName'),
                    email: 'admin@tanda.com',
                    avatar: '',
                    status: 'online'
                };

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    goToProfile(): void {
        this._router.navigate(['/tandaplus/perfil']);
    }

    goToPro(): void {
        this._router.navigate(['/tandaplus/pro']);
    }

    signOut(): void {
        this._authService.signOut().subscribe(() => {
            this._router.navigate(['/auth/sign-in']);
        });
    }
}
